import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User, Building2, Mail, Phone, MapPin, Upload, X, 
  Sparkles, Brain, Target, Calendar, DollarSign,
  Users, TrendingUp, MessageSquare, Camera, 
  Plus, Minus, Save, Wand2, CheckCircle
} from 'lucide-react';
import { Button } from './ui/Button/Button';
import { cn } from '../utils/cn';
import { PersonalizationData } from '../services/templateService';

interface PersonalizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: PersonalizationData) => void;
  initialData?: Partial<PersonalizationData>;
  templateId?: string;
}

export const PersonalizationModal: React.FC<PersonalizationModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData = {},
  templateId
}) => {
  const [activeTab, setActiveTab] = useState<'basic' | 'advanced' | 'behavioral' | 'photos'>('basic');
  const [data, setData] = useState<PersonalizationData>({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    title: '',
    industry: '',
    location: '',
    ...initialData
  });
  const [isGeneratingInsights, setIsGeneratingInsights] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (field: keyof PersonalizationData, value: any) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayChange = (field: keyof PersonalizationData, index: number, value: string) => {
    const currentArray = (data[field] as string[]) || [];
    const newArray = [...currentArray];
    newArray[index] = value;
    handleInputChange(field, newArray);
  };

  const addArrayItem = (field: keyof PersonalizationData) => {
    const currentArray = (data[field] as string[]) || [];
    handleInputChange(field, [...currentArray, '']);
  };

  const removeArrayItem = (field: keyof PersonalizationData, index: number) => {
    const currentArray = (data[field] as string[]) || [];
    const newArray = currentArray.filter((_, i) => i !== index);
    handleInputChange(field, newArray);
  };

  const handleFileUpload = (file: File, field: 'profilePhoto' | 'companyLogo') => {
    const reader = new FileReader();
    reader.onload = (e) => {
      handleInputChange(field, e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const generateAIInsights = async () => {
    setIsGeneratingInsights(true);
    try {
      // Simulate AI insights generation
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock AI-generated insights based on company/industry
      const mockInsights = {
        painPoints: [
          'Scaling operations efficiently',
          'Improving customer acquisition costs',
          'Streamlining internal processes'
        ],
        goals: [
          'Increase revenue by 40% this year',
          'Expand to new markets',
          'Improve team productivity'
        ],
        interests: [
          'Marketing automation',
          'Data analytics',
          'Team collaboration tools'
        ],
        recentNews: `${data.company} announced expansion into European markets`,
        communicationStyle: 'executive' as const
      };

      setData(prev => ({ ...prev, ...mockInsights }));
    } catch (error) {
      console.error('Failed to generate insights:', error);
    } finally {
      setIsGeneratingInsights(false);
    }
  };

  const handleSave = () => {
    onSave(data);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-gradient-to-br from-neutral-900/95 to-neutral-800/95 backdrop-blur-xl border border-neutral-700/50 rounded-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-700/50">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Recipient Personalization</h2>
              <p className="text-neutral-400 text-sm">Create deeply personalized email experiences</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              onClick={generateAIInsights}
              disabled={!data.company || isGeneratingInsights}
              variant="outline"
              size="sm"
              className="border-purple-500/50 text-purple-400"
            >
              {isGeneratingInsights ? (
                <>
                  <Sparkles className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Brain className="w-4 h-4 mr-2" />
                  AI Insights
                </>
              )}
            </Button>
            <Button onClick={handleSave} className="bg-gradient-to-r from-blue-500 to-purple-600">
              <Save className="w-4 h-4 mr-2" />
              Save & Apply
            </Button>
            <button onClick={onClose} className="p-2 text-neutral-400 hover:text-white rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center space-x-1 bg-neutral-800/50 rounded-xl p-1 m-6 mb-0">
          {[
            { id: 'basic', label: 'Basic Info', icon: User },
            { id: 'advanced', label: 'Company Details', icon: Building2 },
            { id: 'behavioral', label: 'Behavioral Data', icon: Target },
            { id: 'photos', label: 'Photos & Assets', icon: Camera }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={cn(
                'flex items-center space-x-2 px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200',
                activeTab === tab.id
                  ? 'bg-blue-500 text-white shadow-lg'
                  : 'text-neutral-400 hover:text-white hover:bg-neutral-700/50'
              )}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    First Name *
                  </label>
                  <input
                    value={data.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    placeholder="John"
                    className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    Last Name *
                  </label>
                  <input
                    value={data.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    placeholder="Smith"
                    className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    Email *
                  </label>
                  <input
                    value={data.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="john@company.com"
                    type="email"
                    className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    Phone
                  </label>
                  <input
                    value={data.phone || ''}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    Job Title *
                  </label>
                  <input
                    value={data.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="CEO, VP Sales, Marketing Director..."
                    className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    Company *
                  </label>
                  <input
                    value={data.company}
                    onChange={(e) => handleInputChange('company', e.target.value)}
                    placeholder="Company Name Inc."
                    className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    Industry
                  </label>
                  <select
                    value={data.industry}
                    onChange={(e) => handleInputChange('industry', e.target.value)}
                    className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  >
                    <option value="">Select Industry</option>
                    <option value="Technology">Technology</option>
                    <option value="Healthcare">Healthcare</option>
                    <option value="Finance">Finance</option>
                    <option value="Manufacturing">Manufacturing</option>
                    <option value="Retail">Retail</option>
                    <option value="Real Estate">Real Estate</option>
                    <option value="Education">Education</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    Location
                  </label>
                  <input
                    value={data.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    placeholder="San Francisco, CA"
                    className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Advanced Tab */}
          {activeTab === 'advanced' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    Company Size
                  </label>
                  <select
                    value={data.companySize || ''}
                    onChange={(e) => handleInputChange('companySize', e.target.value)}
                    className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  >
                    <option value="">Select Size</option>
                    <option value="1-10 employees">1-10 employees</option>
                    <option value="11-50 employees">11-50 employees</option>
                    <option value="51-200 employees">51-200 employees</option>
                    <option value="201-1000 employees">201-1000 employees</option>
                    <option value="1000+ employees">1000+ employees</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    Annual Revenue
                  </label>
                  <select
                    value={data.revenue || ''}
                    onChange={(e) => handleInputChange('revenue', e.target.value)}
                    className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  >
                    <option value="">Select Revenue</option>
                    <option value="$0-1M">$0-1M</option>
                    <option value="$1M-10M">$1M-10M</option>
                    <option value="$10M-100M">$10M-100M</option>
                    <option value="$100M+">$100M+</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  Recent Company News
                </label>
                <textarea
                  value={data.recentNews || ''}
                  onChange={(e) => handleInputChange('recentNews', e.target.value)}
                  placeholder="Recent funding, product launches, partnerships, awards..."
                  rows={3}
                  className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 resize-none"
                />
              </div>

              {/* Dynamic Arrays */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    Pain Points
                  </label>
                  {(data.painPoints || []).map((painPoint, index) => (
                    <div key={index} className="flex items-center space-x-2 mb-2">
                      <input
                        value={painPoint}
                        onChange={(e) => handleArrayChange('painPoints', index, e.target.value)}
                        placeholder="What challenges are they facing?"
                        className="flex-1 px-4 py-2 bg-neutral-800/50 border border-neutral-700 rounded-lg text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      />
                      <button
                        onClick={() => removeArrayItem('painPoints', index)}
                        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => addArrayItem('painPoints')}
                    className="flex items-center space-x-2 text-blue-400 hover:text-blue-300 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Pain Point</span>
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    Goals & Objectives
                  </label>
                  {(data.goals || []).map((goal, index) => (
                    <div key={index} className="flex items-center space-x-2 mb-2">
                      <input
                        value={goal}
                        onChange={(e) => handleArrayChange('goals', index, e.target.value)}
                        placeholder="What are they trying to achieve?"
                        className="flex-1 px-4 py-2 bg-neutral-800/50 border border-neutral-700 rounded-lg text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      />
                      <button
                        onClick={() => removeArrayItem('goals', index)}
                        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => addArrayItem('goals')}
                    className="flex items-center space-x-2 text-blue-400 hover:text-blue-300 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Goal</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Behavioral Tab */}
          {activeTab === 'behavioral' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    Email Engagement Level
                  </label>
                  <select
                    value={data.emailEngagement || ''}
                    onChange={(e) => handleInputChange('emailEngagement', e.target.value)}
                    className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  >
                    <option value="">Select Engagement</option>
                    <option value="high">High - Frequently opens & clicks</option>
                    <option value="medium">Medium - Occasional engagement</option>
                    <option value="low">Low - Rarely engages</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    Communication Style
                  </label>
                  <select
                    value={data.communicationStyle || ''}
                    onChange={(e) => handleInputChange('communicationStyle', e.target.value)}
                    className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  >
                    <option value="">Select Style</option>
                    <option value="formal">Formal - Traditional business language</option>
                    <option value="casual">Casual - Friendly and approachable</option>
                    <option value="technical">Technical - Data-driven and detailed</option>
                    <option value="executive">Executive - Direct and high-level</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">
                  Interests & Preferences
                </label>
                {(data.interests || []).map((interest, index) => (
                  <div key={index} className="flex items-center space-x-2 mb-2">
                    <input
                      value={interest}
                      onChange={(e) => handleArrayChange('interests', index, e.target.value)}
                      placeholder="Professional interests, hobbies, preferences..."
                      className="flex-1 px-4 py-2 bg-neutral-800/50 border border-neutral-700 rounded-lg text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                    />
                    <button
                      onClick={() => removeArrayItem('interests', index)}
                      className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => addArrayItem('interests')}
                  className="flex items-center space-x-2 text-blue-400 hover:text-blue-300 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Interest</span>
                </button>
              </div>
            </div>
          )}

          {/* Photos Tab */}
          {activeTab === 'photos' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-3">
                    Profile Photo
                  </label>
                  <div className="border-2 border-dashed border-neutral-600 rounded-xl p-6 text-center hover:border-blue-500/50 transition-colors">
                    {data.profilePhoto ? (
                      <div className="space-y-3">
                        <img
                          src={data.profilePhoto}
                          alt="Profile"
                          className="w-24 h-24 rounded-full mx-auto object-cover"
                        />
                        <button
                          onClick={() => handleInputChange('profilePhoto', '')}
                          className="text-red-400 text-sm hover:text-red-300"
                        >
                          Remove Photo
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <Camera className="w-12 h-12 text-neutral-400 mx-auto" />
                        <div>
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            className="text-blue-400 hover:text-blue-300 font-medium"
                          >
                            Upload Profile Photo
                          </button>
                          <p className="text-neutral-500 text-sm mt-1">
                            For personalized email signatures
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(file, 'profilePhoto');
                    }}
                    className="hidden"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-3">
                    Company Logo
                  </label>
                  <div className="border-2 border-dashed border-neutral-600 rounded-xl p-6 text-center hover:border-blue-500/50 transition-colors">
                    {data.companyLogo ? (
                      <div className="space-y-3">
                        <img
                          src={data.companyLogo}
                          alt="Company Logo"
                          className="w-24 h-16 mx-auto object-contain bg-white rounded p-2"
                        />
                        <button
                          onClick={() => handleInputChange('companyLogo', '')}
                          className="text-red-400 text-sm hover:text-red-300"
                        >
                          Remove Logo
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <Building2 className="w-12 h-12 text-neutral-400 mx-auto" />
                        <div>
                          <button
                            onClick={() => logoInputRef.current?.click()}
                            className="text-blue-400 hover:text-blue-300 font-medium"
                          >
                            Upload Company Logo
                          </button>
                          <p className="text-neutral-500 text-sm mt-1">
                            For email header branding
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(file, 'companyLogo');
                    }}
                    className="hidden"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}; 